import { FontIcon } from '@intigral/shared';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IStore } from '../../../interfaces/StoreTypes';
import { __ } from '../../../translations';
import { useInterval } from '@intigral/hooks';
import h from '../../../helpers/common';
import { container } from '@intigral/domain';
import { IUserRevisionService } from '@intigral/domain/lib/application/services/UserRevisionService';
import { DI } from '@intigral/constant/config/DIContainer';
import usePPVInfo from '../../../hooks/usePPVInfo';
import url from '../../../helpers/url';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { toast } from '@intigral/shared';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { usePPVHandlers } from './usePPVHandler';
import { USER_REVISION_CHECK_REFRESH } from '../constants';
import { useSelectedPPV } from './useSelectedPPVs';

export const usePPVActions = () => {
    // consts and refs
    const searchString = new URLSearchParams(window.location.search);
    const type = url.getQuery('type');

    // state
    const [selectedPPVEvent, setSelectedPPVEvent] = useState<any>(null);
    const [selectedPPVId, setSelectedPPVId] = useState<string | null>(null);
    const [showppvPurchaseOverlay, setShowppvPurchaseOverlay] = useState(false);
    const [showppvQrModal, setShowppvQrModal] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [selectedChannel, setSelectedChannel] = useState(null);

    // redux state
    const movieOverlay = useSelector((state: any) => state.movieOverlay);
    const currentRevision = useSelector((state: IStore) => state.revision);

    // hooks
    const formRef = useRef<HTMLFormElement>(null);
    const { track } = useContext(MixpanelContext) || {};
    const { ppvSubscribedList, setPpvSubscribedList } = usePPVInfo();

    const {
        addQueryParams,
        getProgramByGuid,
        handleCCPaymentCompleted,
        onPurchaseSuccess,
        handleCloseOverlay,
        handleClickPlay,
        handleClickPurchase,
    } = usePPVHandlers({
        selectedPPVEvent,
        selectedChannel,
        formRef,
        setSelectedProgram,
        setPpvSubscribedList,
        setSelectedPPVId,
        setShowppvPurchaseOverlay,
        setShowppvQrModal,
    });

    useSelectedPPV(
        selectedPPVId,
        setSelectedChannel,
        ppvSubscribedList,
        onPurchaseSuccess,
        selectedPPVEvent,
        setSelectedPPVEvent
    );

    // keep check for revision api to see if there is a change
    const { start, stop } = useInterval(() => {
        const userRevisionSrvice = container.resolve<IUserRevisionService>(DI.SERVICES.IUserRevisionService);
        const accountId = h.getAccountId();
        const bucketId = h.getBucketIdFromAccountId(accountId);
        userRevisionSrvice.getUserRevision(bucketId).then(async (results) => {
            const userRevision = results[accountId];
            if (userRevision) {
                if (userRevision?.subRev > currentRevision?.subRev) {
                    toast(__('succes_message_user_purchased_from_mobile'), 'default', {
                        notDismissible: true,
                        icon: <FontIcon name="check-circle" size="large" md="large" color="green" />,
                    });
                    setShowppvQrModal(false);
                }
            }
        });
    }, USER_REVISION_CHECK_REFRESH);

    // UseEffects
    useEffect(() => {
        const ppvEvent = url.getQuery('eventId');
        if (ppvEvent) {
            setSelectedPPVId(ppvEvent);
            addQueryParams({ eventId: ppvEvent });
        }
        if (!ppvEvent) {
            setSelectedPPVId(ppvEvent);
        }
    }, [movieOverlay?.isPpvVisible, searchString?.toString()]);

    useEffect(() => {
        if (selectedPPVEvent) {
            getProgramByGuid(selectedPPVEvent?.programGuid);
        }
    }, [selectedPPVEvent]);

    useEffect(() => {
        if (window.location.search.includes('success=true')) {
            handleCCPaymentCompleted();
        }
    }, []);

    useEffect(() => {
        if (showppvQrModal) {
            start();
            return () => {
                stop();
            };
        } else {
            stop();
        }
    }, [showppvQrModal]);

    useEffect(() => {
        if (selectedPPVEvent) {
            track(EVENT.PPV.PPV_EVENT_VIEWED, {
                'Event Title': selectedPPVEvent?.name,
                'Event ID': selectedPPVEvent?.eventId,
                initiatedFrom: type,
            });
        }
    }, [selectedPPVEvent]);

    return {
        selectedPPVEvent,
        selectedProgram,
        ppvSubscribedList,
        selectedPPVId,
        showppvPurchaseOverlay,
        showppvQrModal,
        formRef,
        setShowppvPurchaseOverlay,
        setShowppvQrModal,
        handleCloseOverlay,
        handleClickPlay,
        handleClickPurchase,
        onPurchaseSuccess,
        selectedChannel,
    };
};
